<script lang="ts">
export type VPConfirmationDialogAction = 'confirm' | 'cancel';

export type VPConfirmationDialogOutcome = {
  action: VPConfirmationDialogAction;
  checked: boolean;
};
</script>

<script setup lang="ts">
import { type Ref, inject, ref, useId, watch } from 'vue';
import { useForm } from 'vee-validate';
import { useGettext } from 'vue3-gettext';
import VPButton from './VPButton.vue';
import VPModal from './VPModal.vue';
import VPToggle from './form/VPToggle.vue';

type Props = {
  show: boolean;
  title: string;
  message?: string;
  confirmLabel: string;
  cancelLabel?: string;
  checkboxLabel?: string;
  zIndexLevel?: number;
  action?: (outcome: VPConfirmationDialogOutcome) => unknown;
};

const props = withDefaults(defineProps<Props>(), {
  message: undefined,
  cancelLabel: undefined,
  checkboxLabel: undefined,
  zIndexLevel: 1,
  action: undefined,
});

const emit = defineEmits<{
  (e: 'update:show', show: boolean): void;
  (e: 'close', outcome: VPConfirmationDialogOutcome, failure?: { error: unknown }): void;
}>();

const { $gettext } = useGettext();

// A custom-element host provides its own target: `document.querySelector` cannot see into a shadow root.
const teleportTarget = inject<Readonly<Ref<HTMLElement | null>> | null>('vpModalTeleportTarget', null);
const titleId = useId();
const { values, resetForm } = useForm<{ checked: boolean }>({ initialValues: { checked: false } });
const running = ref(false);

watch(() => props.show, open => {
  if (open) {
    running.value = false;
    resetForm({ values: { checked: false } });
  }
});

const emitClose = (outcome: VPConfirmationDialogOutcome, failure?: { error: unknown }) => {
  emit('update:show', false);

  if (failure) {
    emit('close', outcome, failure);
  } else {
    emit('close', outcome);
  }
};

const cancel = () => {
  if (!running.value) {
    emitClose({ action: 'cancel', checked: !!values.checked });
  }
};

const confirm = async () => {
  if (running.value) {
    return;
  }

  const outcome: VPConfirmationDialogOutcome = { action: 'confirm', checked: !!values.checked };

  if (!props.action) {
    emitClose(outcome);

    return;
  }

  running.value = true;
  let failure: { error: unknown } | undefined;

  try {
    await props.action(outcome);
  } catch (error) {
    failure = { error };
  }

  running.value = false;
  emitClose(outcome, failure);
};

// The `!` overrides beat VPModal's hardcoded card classes; see VPTurnstileModal.
</script>

<template>
  <VPModal
    :show="show"
    :teleport-target="teleportTarget ?? undefined"
    :z-index-level="zIndexLevel"
    hide-actions
    role="dialog"
    aria-modal="true"
    :aria-labelledby="titleId"
    :backdrop-clickable="!running"
    class="w-[416px] max-w-full min-w-0! rounded-md! border border-neutral-300 bg-neutral-0! p-0! gap-0! shadow-vp-large"
    @close="cancel"
    @backdrop-click="cancel"
  >
    <div class="flex flex-col gap-6 px-6 pt-4 pb-8">
      <div class="flex flex-col gap-4">
        <div class="flex items-center justify-between gap-4">
          <h2
            :id="titleId"
            class="text-2xl font-bold text-neutral-900"
          >
            {{ title }}
          </h2>
          <VPButton
            variant="ghost-neutral"
            size="sm"
            icon-prefix="mdi:close"
            class="-m-2 text-2xl!"
            :aria-label="$gettext('Close')"
            :disabled="running"
            @click="cancel"
          />
        </div>
        <div
          v-if="message || $slots.default"
          class="text-base text-neutral-700"
        >
          <slot>{{ message }}</slot>
        </div>
      </div>

      <div class="flex flex-col gap-6">
        <div class="flex gap-4">
          <VPButton
            v-if="cancelLabel"
            variant="outline"
            size="lg"
            class="flex-1"
            :label="cancelLabel"
            :disabled="running"
            @click="cancel"
          />
          <VPButton
            variant="default"
            size="lg"
            class="flex-1"
            :label="confirmLabel"
            :loading="running"
            @click="confirm"
          />
        </div>

        <VPToggle
          v-if="checkboxLabel"
          name="checked"
          variant="checkbox"
          :label="checkboxLabel"
          :disabled="running"
        />
      </div>
    </div>
  </VPModal>
</template>
